import {
  backOut,
  clampAlpha,
  emphasizedAccelerate,
  emphasizedDecelerate,
  keyframes,
  linear,
  progressAt,
  shakeOffset,
  slam,
} from 'modules/match-video/domain/scenes/kit';

/** The exit of a set B plate, counted back from the end of its window: the whole plate flies left (`.x-left`). */
export const SLASH_PLATE_EXIT_SEC = 0.35;
/** `toLeft`: 900 px to the left, fading out. */
const EXIT_DISTANCE = 900;

export interface Slide {
  dx: number;
  alpha: number;
}

/** A speed streak across the tag: shift from its left edge, opacity. Hidden (0) before and after its run. */
export interface Streak {
  dx: number;
  alpha: number;
}

export interface SlashExit {
  /** Shift of the whole plate (and the minute) to the left on the exit, px (≤ 0). */
  dx: number;
  alpha: number;
}

export interface SlashGoalPlateState {
  exit: SlashExit;
  shake: { dx: number; dy: number };
  tag: Slide;
  streaks: Streak[];
  word: { scale: number; alpha: number };
  body: Slide;
  number: Slide;
  playerRise: number;
  teamRise: number;
  /** `drop` of the minute cell: shift up, px, and opacity. */
  minute: { dy: number; alpha: number };
}

export interface SlashDangerPlateState {
  exit: SlashExit;
  tag: Slide;
  /** Opacity of the white flash over the tag (three blinks). */
  flash: number;
  body: Slide;
  textRise: number;
}

const dec = (t: number, delaySec: number, durationSec: number) => emphasizedDecelerate(progressAt(t, delaySec, durationSec));

/** `fromL` on `--dec` or `--back`: from `distance` px to the left, fading in; an overshot opacity is clamped. */
function fromLeft(t: number, delaySec: number, durationSec: number, distance: number, ease = emphasizedDecelerate): Slide {
  const p = ease(progressAt(t, delaySec, durationSec));
  return { dx: distance * (p - 1), alpha: clampAlpha(p) };
}

/** `streak` .4 s: −500 → 700 px in one `--dec` segment; opacity 0 → 1 by 30 %, back to 0 at 100 %, each `--dec`. */
function streak(t: number, delaySec: number): Streak {
  const end = delaySec + 0.4;
  if (t < delaySec || t >= end) return { dx: -500, alpha: 0 };
  return {
    dx: -500 + 1200 * dec(t, delaySec, 0.4),
    alpha: keyframes(t, [
      { atSec: delaySec, value: 0, ease: emphasizedDecelerate },
      { atSec: delaySec + 0.12, value: 1, ease: emphasizedDecelerate },
      { atSec: end, value: 0 },
    ]),
  };
}

/** `windowSec - (windowSec - exit)` is not exactly `exit` in floating point: the last frame would keep a sliver. */
const snapToZero = (value: number) => (value < 1e-9 ? 0 : value);

/**
 * When the exit starts the entrance classes are gone: every entering element jumps to its final state, as in the
 * mockup where `.out` replaces `.play`. The entrance is read at `t`, or at its end once the exit has begun.
 */
function exitState(t: number, windowSec: number): { entranceT: number; exit: SlashExit } {
  const exitAt = windowSec - SLASH_PLATE_EXIT_SEC;
  if (t < exitAt) return { entranceT: t, exit: { dx: 0, alpha: 1 } };
  const p = emphasizedAccelerate(progressAt(t - exitAt, 0, SLASH_PLATE_EXIT_SEC));
  return { entranceT: Number.POSITIVE_INFINITY, exit: { dx: -EXIT_DISTANCE * p, alpha: snapToZero(1 - p) } };
}

/** The goal plate of set B at `t` seconds since it appeared, in a window of `windowSec`. */
export function slashGoalPlateState(t: number, windowSec: number): SlashGoalPlateState {
  const { entranceT: e, exit } = exitState(t, windowSec);
  const minute = backOut(progressAt(e, 0.6, 0.35));
  return {
    exit,
    shake: shakeOffset(e, 0.3),
    tag: fromLeft(e, 0, 0.35, 520, backOut),
    streaks: [streak(e, 0.1), streak(e, 0.16), streak(e, 0.22)],
    word: slam(e, 0.2, 0.4),
    body: fromLeft(e, 0.15, 0.4, 120),
    number: fromLeft(e, 0.35, 0.4, 60),
    playerRise: dec(e, 0.4, 0.45),
    teamRise: dec(e, 0.48, 0.45),
    minute: { dy: 60 * (minute - 1), alpha: clampAlpha(minute) },
  };
}

const FLASH_AT = 0.4;
const FLASH_SEC = 0.28;
const FLASH_RUNS = 3;

/** The dangerous-moment plate of set B: a yellow tag blinking white three times, the dark body with both teams. */
export function slashDangerPlateState(t: number, windowSec: number): SlashDangerPlateState {
  const { entranceT: e, exit } = exitState(t, windowSec);
  const since = e - FLASH_AT;
  const running = since >= 0 && since < FLASH_SEC * FLASH_RUNS;
  // `flash`: opacity 0 → .85 at 50 % → 0, linear.
  const k = running ? linear((since % FLASH_SEC) / FLASH_SEC) : 0;
  return {
    exit,
    tag: fromLeft(e, 0, 0.35, 400, backOut),
    flash: running ? 0.85 * (1 - Math.abs(2 * k - 1)) : 0,
    body: fromLeft(e, 0.15, 0.4, 120),
    textRise: dec(e, 0.35, 0.45),
  };
}
